import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Platform,
  TextInput,
  Dimensions,
  ActivityIndicator,
  SafeAreaView,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import {
    BottomTabs,
    ConfirmationModal,
    Header,
    MoreOptionsModal,
    ScreenLayout,
    StepperReturnBanner,
    colors,
    spacing,
    typography
} from '../components';
import { WhatsAppPhoneList } from '../components/whatsapp/WhatsAppPhoneList';
import { WhatsAppPhoneCard, WhatsAppPhoneCardData } from '../components/whatsapp/WhatsAppPhoneCard';
import { WhatsAppPhoneSettings, PhoneSettingsData } from '../components/whatsapp/WhatsAppPhoneSettings';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { apiService, syncApiServiceWithStore } from '../services/api';
import { useApiSync } from '../hooks/useApiSync';
import { PhoneInputComponent } from '../components/design-system/PhoneInput';
import { OTPInput } from '../components/OTPInput';
import { shadow } from '../theme/colors';
import { decodeJWT, checkWhatsAppConfiguration } from '../utils/token-utils';
import { tokenManager } from '../services/token-manager';
import { notificationService } from '../services/notification.service';
import { useThemedStyles } from '../hooks/useThemedStyles';

// Conditional WebView import - only for native platforms (mirrors PaymentScreen.tsx's pattern)
let WebView: any = null;
if (Platform.OS !== 'web') {
    WebView = require('react-native-webview').WebView;
}

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// Detect if we're on mobile browser (not just web platform)
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768 || screenHeight < 768);

export const WhatsAppBusinessSetupScreen: React.FC = () => {
    const styles = useThemedStyles(getStyles);
    const { user, tokens, updateAuthTokens, currentBusiness } = useAppStore();
    const { showNotification, navigateTo, activeTab, setActiveTab, routeParams } = useAppContext();
    // Embedded inside the channel onboarding stepper, the caller passes where "done" should
    // go next (the next step, not always Home) via routeParams.onCompleteScreen/onCompleteParams.
    const onCompleteScreen = (routeParams as any)?.onCompleteScreen || 'home';
    const onCompleteParams = (routeParams as any)?.onCompleteParams;
    
    // CRITICAL: Auto-sync tokens to API service whenever they change
    useApiSync();

    // Set active tab to home on mount
    useEffect(() => {
        setActiveTab('home');
    }, [setActiveTab]);

    // Screen states
    const [currentStep, setCurrentStep] = useState<'phone' | 'otp' | 'success'>('phone');
    const [isLoading, setIsLoading] = useState(false);
    const [showMoreModal, setShowMoreModal] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState<WhatsAppPhoneCardData | null>(null);
    const [showRightPane, setShowRightPane] = useState(false);
    const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');

    // Phone numbers state
    const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
    const [isLoadingPhoneNumbers, setIsLoadingPhoneNumbers] = useState(false);
    const [phoneToDelete, setPhoneToDelete] = useState<WhatsAppPhoneCardData | null>(null);
    const [isDeletingPhone, setIsDeletingPhone] = useState(false);
    const [registeringPhoneId, setRegisteringPhoneId] = useState<string | null>(null);
    const [autoRefreshInterval, setAutoRefreshInterval] = useState<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);
    const notifiedPhoneIds = useRef<Set<string>>(new Set()); // Track which phones we've already notified about

    // Phone number state
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberError, setPhoneNumberError] = useState('');
    const phoneInputRef = useRef<{
        getValue: () => {
            formattedNumber: string;
            dialCode: string;
            countryCode: string;
            nationalNumber: string;
            country: string;
        };
        getDialCode: () => string;
        getCountryCode: () => string;
        inputRef: React.RefObject<TextInput>
    }>(null);

    // OTP state
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [registrationId, setRegistrationId] = useState('');
    const [codeMethod, setCodeMethod] = useState<'SMS' | 'VOICE'>('SMS');
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState(0);
    const otpInputRefs = useRef<(TextInput | null)[]>([]);
    const [submittedPhoneData, setSubmittedPhoneData] = useState<{
        dialCode: string;
        nationalNumber: string;
        formattedNumber: string;
    } | null>(null);

    // Business info - get from user context or use default
    const [businessName, setBusinessName] = useState('');

    // Existing phone numbers and limits
    const [existingPhoneNumbers, setExistingPhoneNumbers] = useState<Array<{
        whatsapp_phone_number: string;
        whatsapp_business_account_id: string;
        whatsapp_phone_number_id: string;
        status: string;
        registered_at: string;
        is_default?: boolean;
    }>>([]);
    const [hasExistingNumbers, setHasExistingNumbers] = useState(false);
    const [phoneNumberLimit, setPhoneNumberLimit] = useState(1);
    const [canAddMoreNumbers, setCanAddMoreNumbers] = useState(true);
    const [showPhoneForm, setShowPhoneForm] = useState(false);

    // Embedded signup: connect a business's own WhatsApp Business Account via Meta's popup,
    // instead of registering a new number under our shared WABA.
    const [showEmbeddedSignup, setShowEmbeddedSignup] = useState(false);
    // The popup/WebView is a separate page context with no access to the app's stored JWT —
    // it can't attach an Authorization header on its own, so the token is passed via the URL
    // and the page echoes it back as a Bearer header when it calls our /complete endpoint.
    const embeddedSignupUrl = `${apiService.getBaseURL()}/public/whatsapp-embedded-signup.html?token=${encodeURIComponent(apiService.getAuthToken() || '')}`;

    // Function to fetch phone numbers from API
    const fetchPhoneNumbers = async () => {
        try {
            setIsLoadingPhoneNumbers(true);
            const response = await apiService.getWhatsAppPhoneNumbers();
            
            if (response.statusCode === 1000 && response.data) {
                setPhoneNumbers(response.data);
                
                // Check if any phone numbers were verified by Meta admin
                await checkMetaVerificationStatus(response.data);
            } else {
                console.log('⚠️ [WhatsAppBusinessSetup] No phone numbers found or error:', response);
            }
        } catch (error) {
            console.error('❌ [WhatsAppBusinessSetup] Error fetching phone numbers:', error);
        } finally {
            setIsLoadingPhoneNumbers(false);
        }
    };

    // Handles both the web `window.postMessage` and native WebView `onMessage` shapes from
    // whatsapp-embedded-signup.html — mirrors PaymentScreen.tsx's paymob_success/failure pattern.
    const handleEmbeddedSignupResult = (data: any) => {
        if (data?.type === 'whatsapp_embedded_signup_success') {
            setShowEmbeddedSignup(false);
            showNotification('success', 'WhatsApp Connected', 'Your WhatsApp Business Account has been connected successfully.');
            fetchPhoneNumbers();
            // Opened from onboarding: go back to the channel list, like Facebook does.
            if ((routeParams as any)?.onCompleteScreen) {
                setTimeout(() => navigateTo(onCompleteScreen, undefined, onCompleteParams), 1500);
            }
        } else if (data?.type === 'whatsapp_embedded_signup_failure') {
            setShowEmbeddedSignup(false);
            showNotification('error', 'Connection Failed', data?.error || 'Failed to connect your WhatsApp Business Account.');
        }
    };

    useEffect(() => {
        if (Platform.OS !== 'web' || !showEmbeddedSignup) return;
        const handler = (event: MessageEvent) => handleEmbeddedSignupResult(event.data);
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [showEmbeddedSignup]);

    const handleEmbeddedSignupWebViewMessage = (event: any) => {
        try {
            const data = JSON.parse(event.nativeEvent.data);
            handleEmbeddedSignupResult(data);
        } catch (e) {
            console.error('[WhatsAppBusinessSetup] Failed to parse embedded signup message:', e);
        }
    };

    // Function to check Meta verification status and update internally
    const checkMetaVerificationStatus = async (phoneNumbers: any[]) => {
        try {
            for (const phone of phoneNumbers) {
                // Check if phone is verified from either isVerified flag or status field
                const isPhoneVerified = phone.isVerified || phone.status === 'verified' || phone.status === 'VERIFIED';
                const isMetaConnected = phone.status === 'CONNECTED' || phone.status === 'verified' || phone.status === 'VERIFIED';
                
                console.log('🔍 [WhatsAppBusinessSetup] Checking Meta verification:', {
                    phone: phone.displayPhoneNumber,
                    isVerified: phone.isVerified,
                    status: phone.status,
                    codeVerificationStatus: phone.codeVerificationStatus,
                    verifiedBy: phone.verifiedBy,
                    isPhoneVerified,
                    isMetaConnected
                });
                
                // Only complete verification if:
                // 1. Phone is verified by Meta (status or isVerified)
                // 2. Code verification status is VERIFIED
                // 3. NOT already verified by meta_admin (prevent loop!)
                if (isPhoneVerified && isMetaConnected && phone.codeVerificationStatus === 'VERIFIED' && phone.verifiedBy !== 'meta_admin') {
                    console.log('✅ [WhatsAppBusinessSetup] Phone number verified by Meta admin, completing internal verification:', phone.displayPhoneNumber);
                    
                    // Complete the verification process internally (without refresh to prevent loop)
                    await completeMetaVerification(phone);
                } else if (phone.verifiedBy === 'meta_admin') {
                    console.log('✅ [WhatsAppBusinessSetup] Phone already verified by meta_admin, skipping:', phone.displayPhoneNumber);
                }
            }
        } catch (error) {
            console.error('❌ [WhatsAppBusinessSetup] Error checking Meta verification status:', error);
        }
    };

    // Function to complete verification when Meta admin has verified
    const completeMetaVerification = async (phone: any) => {
        try {
            console.log('🔄 [WhatsAppBusinessSetup] Completing verification for Meta-verified phone:', phone.displayPhoneNumber);
            
            // Update the phone number status internally
            const updateResponse = await apiService.updatePhoneNumberVerificationStatus(phone.id, {
                verified: true,
                verifiedAt: new Date().toISOString(),
                status: 'verified',
                verifiedBy: 'meta_admin'
            });
            
            if (updateResponse.statusCode === 1000) {
                console.log('✅ [WhatsAppBusinessSetup] Phone number verification completed internally');
                
                // Only show notification if we haven't notified about this phone before
                if (!notifiedPhoneIds.current.has(phone.id)) {
                    notificationService.showNewMessageNotification(
                        phone.id, // conversationId
                        `WhatsApp Business (${phone.displayPhoneNumber})`, // contactName
                        'Phone number verified by Meta admin and is now ready to use!', // message
                        'whatsapp' // channel
                    );
                    
                    // Mark this phone as notified
                    notifiedPhoneIds.current.add(phone.id);
                    console.log('✅ [WhatsAppBusinessSetup] Notification shown and phone marked as notified');
                } else {
                    console.log('ℹ️ [WhatsAppBusinessSetup] Phone already notified, skipping notification:', phone.id);
                }
                
                // Don't refresh immediately to prevent loop - the auto-refresh will pick it up
                // await fetchPhoneNumbers(); // ❌ REMOVED - This was causing the loop!
                console.log('✅ [WhatsAppBusinessSetup] Verification complete, auto-refresh will update UI');
            } else {
                console.log('⚠️ [WhatsAppBusinessSetup] Failed to update internal verification status');
            }
        } catch (error) {
            console.error('❌ [WhatsAppBusinessSetup] Error completing Meta verification:', error);
        }
    };

    // Get business name from user context and check existing numbers
    useEffect(() => {
        const checkBusinessAndNumbers = async () => {
            try {
                // Get current token from store
                const currentToken = useAppStore.getState().tokens?.token;

                if (currentToken) {
                    // Decode token to get business info and subscription data
                    const decodedToken = decodeJWT(currentToken);

                    if (decodedToken) {
                        // Auto-populate business name from token
                        if (decodedToken.business?.name) {
                            setBusinessName(decodedToken.business.name);
                            console.log('✅ [WhatsAppBusinessSetup] Business name auto-populated:', decodedToken.business.name);
                        }

                        // Check WhatsApp configuration and existing numbers
                        const whatsappConfig = checkWhatsAppConfiguration(currentToken);

                        if (whatsappConfig.hasMetadata && whatsappConfig.phones && whatsappConfig.phones.length > 0) {
                            setExistingPhoneNumbers(whatsappConfig.phones);
                            setHasExistingNumbers(true);
                            console.log('✅ [WhatsAppBusinessSetup] Found existing phone numbers:', whatsappConfig.phones.length);
                        }

                        // Get phone number limit from subscription
                        if (decodedToken.subscription?.services?.wabu?.number_of_whatsapp_numbers) {
                            const limit = decodedToken.subscription.services.wabu.number_of_whatsapp_numbers;
                            setPhoneNumberLimit(limit);
                            setCanAddMoreNumbers(whatsappConfig.phoneCount < limit);
                            console.log('🔍 [WhatsAppBusinessSetup] Phone number limit:', limit, 'Current count:', whatsappConfig.phoneCount);
                        }
                    }
                }
            } catch (error) {
                console.error('❌ [WhatsAppBusinessSetup] Error checking business and numbers:', error);
            }
        };

        checkBusinessAndNumbers();
        
        // Also fetch phone numbers from API to get latest verification status
        fetchPhoneNumbers();
        
        // Set up auto-refresh to check for Meta verification status every 30 seconds
        // Only run when component is mounted
        const interval = setInterval(() => {
            if (isMountedRef.current) {
                fetchPhoneNumbers();
            }
        }, 30000); // Check every 30 seconds
        
        setAutoRefreshInterval(interval);
        
        // Cleanup interval on unmount
        return () => {
            isMountedRef.current = false;
            if (interval) {
                clearInterval(interval);
            }
        };
    }, []);

    const handlePhoneSubmit = async () => {
        // Validate phone number
        if (!phoneNumber.trim()) {
            setPhoneNumberError('Phone number is required');
            return;
        }

        if (phoneNumber.length < 5) {
            setPhoneNumberError('Please enter a valid phone number');
            return;
        }

        // Get the phone number object with all components
        const phoneData = phoneInputRef.current?.getValue();
        
        if (!phoneData || !phoneData.dialCode || !phoneData.nationalNumber) {
            setPhoneNumberError('Please enter a valid phone number');
            setIsLoading(false);
            return;
        }

        // Extract numeric country code from dialCode (e.g., "+20" -> "20")
        const numericCountryCode = phoneData.dialCode.replace(/^\+/, ''); // Remove leading +
        const nationalNumber = phoneData.nationalNumber.replace(/\D/g, ''); // Remove all non-digits

        // Validate national number
        if (!nationalNumber || nationalNumber.length < 4) {
            setPhoneNumberError('Please enter a valid phone number');
            setIsLoading(false);
            return;
        }

        // Construct phone number in API format: country code + number (e.g., "201023356631")
        // API expects digits only, no +, no spaces, no dashes
        const apiPhoneNumber = numericCountryCode + nationalNumber;

        // Validate format (API expects 8-15 digits, starting with 1-9)
        if (!apiPhoneNumber || apiPhoneNumber.length < 8 || apiPhoneNumber.length > 15) {
            setPhoneNumberError('Please enter a valid phone number with country code');
            setIsLoading(false);
            return;
        }

        if (!/^[1-9]\d{7,14}$/.test(apiPhoneNumber)) {
            setPhoneNumberError('Invalid phone number format. Please include country code.');
            setIsLoading(false);
            return;
        }

        // Prepare phoneData object for backend
        // Backend expects countryCode as numeric string (e.g., "20"), not ISO code (e.g., "EG")
        const phoneDataForApi = {
            ...phoneData,
            countryCode: numericCountryCode, // Override ISO countryCode with numeric code
            dialCode: phoneData.dialCode, // Keep dialCode with + for display
            nationalNumber: nationalNumber, // Clean national number
        };

        // Store phone data for display in OTP step
        setSubmittedPhoneData({
            dialCode: phoneData.dialCode,
            nationalNumber: nationalNumber,
            formattedNumber: phoneData.formattedNumber
        });

        console.log('🔍 [WhatsAppBusinessSetup] Phone number data:', {
            rawPhoneNumber: phoneNumber,
            phoneData: phoneData,
            phoneDataForApi: phoneDataForApi,
            apiPhoneNumber: apiPhoneNumber,
            numericCountryCode: numericCountryCode,
            nationalNumber: nationalNumber,
            hasPhoneInputRef: !!phoneInputRef.current
        });

        setPhoneNumberError('');
        setIsLoading(true);

        try {
            // Call API to register phone number with Meta using structured phone data
            // Backend will use phoneDataForApi.countryCode (numeric) and phoneDataForApi.nationalNumber
            const response = await apiService.registerWhatsAppPhoneNumber(
                businessName || 'Business Account',
                apiPhoneNumber, // Send in API format: digits only (for validation)
                phoneDataForApi // Send structured data with numeric countryCode
            );

            console.log('🔍 [WhatsAppBusinessSetup] Full API response:', {
                statusCode: response.statusCode,
                hasData: !!response.data,
                dataStatus: response.data?.status,
                hasErrors: !!response.errors,
                errors: response.errors,
                fullResponse: JSON.stringify(response, null, 2)
            });

            // Check for "already in use" status first (even if statusCode is 1001 or errors array exists, it's a handled case, not an error)
            // This must be checked BEFORE checking response.errors to prevent false error handling
            // Also check statusCode 1001 as an indicator
            const isAlreadyInUse = (response.data && response.data.status === 'already_in_use') || 
                                   (response.statusCode === 1001 && response.data?.status === 'already_in_use');
            
            if (isAlreadyInUse) {
                console.log('✅ [WhatsAppBusinessSetup] Detected "already_in_use" status, handling as informational case (not error)');
                const userMessage = response.data.actionDescription || response.data.message || 
                    'This phone number is already registered to a WhatsApp account. To use this number, disconnect it from the existing account. Then, return to this page and try again. It can take up to three minutes for the number to refresh.';
                
                // Show notification and alert (no error styling)
                showNotification('warning', 'Phone Number Already in Use', userMessage);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        'Phone Number Already in Use',
                        userMessage,
                        [
                            {
                                text: 'I Understand',
                                onPress: () => {
                                    // Don't set error - just clear the form
                                    setPhoneNumber('');
                                }
                            },
                            {
                                text: 'Use Different Number',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            }
                        ]
                    );
                }
                // Clear the form without error styling
                setPhoneNumber('');
                setIsLoading(false);
                return;
            }

            // Check for errors in errors array (only if not "already in use")
            if (response.errors && response.errors.length > 0) {
                const errorObj = response.errors[0];
                const errorMessage = typeof errorObj === 'string' ? errorObj : (errorObj?.message || '');
                
                // Check if error indicates phone number already in use (fallback check)
                if (errorMessage.includes('already registered') || 
                    errorMessage.includes('already in use') ||
                    errorMessage.includes('Phone number in use') ||
                    errorMessage.includes('disconnect it from the existing account')) {
                    
                    // Extract user-friendly message if available
                    let userMessage = 'This phone number is already registered to a WhatsApp account. To use this number, disconnect it from the existing account. Then, return to this page and try again. It can take up to three minutes for the number to refresh.';
                    
                    // Try to get more specific message from error
                    if (errorMessage.includes('disconnect it from the existing account')) {
                        userMessage = errorMessage;
                    }
                    
                    // Show notification and alert (no error styling)
                    showNotification('warning', 'Phone Number Already in Use', userMessage);
                    if (Platform.OS !== 'web') {
                        Alert.alert(
                            'Phone Number Already in Use',
                            userMessage,
                            [
                                {
                                    text: 'I Understand',
                                    onPress: () => {
                                        setPhoneNumber('');
                                    }
                                },
                                {
                                    text: 'Use Different Number',
                                    onPress: () => {
                                        setPhoneNumber('');
                                    }
                                }
                            ]
                        );
                    }
                    setIsLoading(false);
                    return;
                }
                
                // Other errors - show notification and alert
                showNotification('error', 'Registration Error', errorMessage || 'Failed to register phone number. Please try again.');
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        'Registration Error',
                        errorMessage || 'Failed to register phone number. Please try again.',
                        [
                            {
                                text: 'OK',
                                onPress: () => {
                                    setPhoneNumberError(errorMessage || 'Failed to register phone number. Please try again.');
                                }
                            }
                        ]
                    );
                } else {
                    setPhoneNumberError(errorMessage || 'Failed to register phone number. Please try again.');
                }
                setIsLoading(false);
                return;
            }

            if (response.data) {
                // Use registrationId if available, otherwise fallback to phoneNumberId
                const id = response.data.registrationId || response.data.phoneNumberId;
                console.log('🔍 [WhatsAppBusinessSetup] Phone registration response:', {
                    registrationId: response.data.registrationId,
                    phoneNumberId: response.data.phoneNumberId,
                    selectedId: id,
                    status: response.data.status
                });
                setRegistrationId(id);
                setCodeMethod(response.data.codeMethod || 'SMS');

                // Phone number available, proceed to OTP step
                if (response.data.status !== 'already_in_use') {
                    // Phone number available, proceed to OTP step
                    setCurrentStep('otp');
                    setOtp(''); // Clear any previous OTP
                    setOtpError(''); // Clear any previous errors
                    if (Platform.OS === 'web') {
                        setShowRightPane(false);
                    }
                    // Auto-focus first OTP input after a short delay
                    setTimeout(() => {
                        otpInputRefs.current[0]?.focus();
                    }, 100);
                    notificationService.showNewMessageNotification(
                        registrationId,
                        `WhatsApp Business (${phoneNumber})`,
                        'Phone number registered! Please check your phone for the verification code from Meta.',
                        'whatsapp'
                    );

                    // Refresh token to get updated subscription data
                    setTimeout(() => {
                        console.log('🔄 [WhatsAppBusinessSetup] Refreshing token after phone registration...');
                        tokenManager.refreshTokens();
                    }, 1000);
                }
            } else {
                // No data and no errors - show notification and alert
                const errorMsg = 'Failed to register phone number. Please try again.';
                showNotification('error', 'Registration Failed', errorMsg);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        'Registration Failed',
                        errorMsg,
                        [
                            {
                                text: 'OK',
                                onPress: () => {
                                    setPhoneNumberError(errorMsg);
                                }
                            }
                        ]
                    );
                } else {
                    setPhoneNumberError(errorMsg);
                }
                setIsLoading(false);
            }

        } catch (error: any) {
            console.error('WhatsApp registration error:', error);
            console.error('WhatsApp registration error details:', {
                message: error.message,
                name: error.name,
                stack: error.stack,
                fullError: error,
                hasData: !!error.data,
                dataStatus: error.data?.status,
                statusCode: error.statusCode,
                hasResponse: !!error.response,
                responseData: error.response?.data
            });

            // FIRST: Check if error actually contains a valid response with "already_in_use" status
            // This can happen if the API service throws an error but includes the response data
            if (error.data && error.data.status === 'already_in_use') {
                console.log('✅ [WhatsAppBusinessSetup] Found "already_in_use" in error.data, handling as informational case');
                const userMessage = error.data.actionDescription || error.data.message || 
                    'This phone number is already registered to a WhatsApp account. To use this number, disconnect it from the existing account. Then, return to this page and try again. It can take up to three minutes for the number to refresh.';
                
                showNotification('warning', 'Phone Number Already in Use', userMessage);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        'Phone Number Already in Use',
                        userMessage,
                        [
                            {
                                text: 'I Understand',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            },
                            {
                                text: 'Use Different Number',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            }
                        ]
                    );
                }
                setPhoneNumber('');
                setIsLoading(false);
                return;
            }

            // Also check error.response.data for "already_in_use"
            if (error.response?.data?.status === 'already_in_use') {
                console.log('✅ [WhatsAppBusinessSetup] Found "already_in_use" in error.response.data, handling as informational case');
                const userMessage = error.response.data.actionDescription || error.response.data.message || 
                    'This phone number is already registered to a WhatsApp account. To use this number, disconnect it from the existing account. Then, return to this page and try again. It can take up to three minutes for the number to refresh.';
                
                showNotification('warning', 'Phone Number Already in Use', userMessage);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        'Phone Number Already in Use',
                        userMessage,
                        [
                            {
                                text: 'I Understand',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            },
                            {
                                text: 'Use Different Number',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            }
                        ]
                    );
                }
                setPhoneNumber('');
                setIsLoading(false);
                return;
            }

            let errorMessage = '';
            let errorTitle = 'Registration Error';

            // Check if error has response data with errors array
            if (error.response?.data?.errors && error.response.data.errors.length > 0) {
                const errorObj = error.response.data.errors[0];
                errorMessage = typeof errorObj === 'string' ? errorObj : (errorObj?.message || '');
            } else if (error.errors && error.errors.length > 0) {
                // Also check error.errors directly (from API service structured errors)
                const errorObj = error.errors[0];
                errorMessage = typeof errorObj === 'string' ? errorObj : (errorObj?.message || '');
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            // Check for network/connection errors (but NOT if it's "already_in_use" case)
            // Only show network error if it's a genuine network issue
            const isNetworkError = errorMessage.includes('Unable to connect') ||
                errorMessage.includes('Network error occurred') ||
                errorMessage.includes('Network error') ||
                (errorMessage.includes('network') && !errorMessage.includes('already')) ||
                (errorMessage.includes('connection') && !errorMessage.includes('already')) ||
                errorMessage.includes('ECONNREFUSED') ||
                errorMessage.includes('ETIMEDOUT') ||
                error.code === 'NETWORK_ERROR' ||
                error.code === 'ERR_NETWORK' ||
                error.name === 'TypeError' ||
                error.name === 'AbortError';

            // Don't treat "already_in_use" messages as network errors
            const isAlreadyInUseMessage = errorMessage.includes('already registered') || 
                errorMessage.includes('already in use') ||
                errorMessage.includes('Phone number in use') ||
                errorMessage.includes('disconnect it from the existing account');

            if (isNetworkError && !isAlreadyInUseMessage) {
                errorTitle = 'Connection Error';
                const networkErrorMsg = 'Unable to connect to server. Please check your internet connection and try again.';
                showNotification('error', errorTitle, networkErrorMsg);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        errorTitle,
                        networkErrorMsg,
                        [
                            {
                                text: 'Retry',
                                onPress: () => {
                                    setPhoneNumberError('');
                                    // User can try again
                                }
                            },
                            {
                                text: 'OK',
                                style: 'cancel',
                                onPress: () => {
                                    setPhoneNumberError(networkErrorMsg);
                                }
                            }
                        ]
                    );
                } else {
                    setPhoneNumberError(networkErrorMsg);
                }
                setIsLoading(false);
                return;
            }

            // Check if error indicates phone number already in use
            if (errorMessage.includes('already registered') || 
                errorMessage.includes('already in use') ||
                errorMessage.includes('Phone number in use') ||
                errorMessage.includes('disconnect it from the existing account')) {
                
                errorTitle = 'Phone Number Already in Use';
                let userMessage = 'This phone number is already registered to a WhatsApp account. To use this number, disconnect it from the existing account. Then, return to this page and try again. It can take up to three minutes for the number to refresh.';
                
                if (errorMessage.includes('disconnect it from the existing account')) {
                    userMessage = errorMessage;
                }
                
                // Show notification and alert (no error styling - just clear the input)
                showNotification('warning', errorTitle, userMessage);
                if (Platform.OS !== 'web') {
                    Alert.alert(
                        errorTitle,
                        userMessage,
                        [
                            {
                                text: 'I Understand',
                                onPress: () => {
                                    // Don't set error - just clear the form
                                    setPhoneNumber('');
                                }
                            },
                            {
                                text: 'Use Different Number',
                                onPress: () => {
                                    setPhoneNumber('');
                                }
                            }
                        ]
                    );
                }
                // Don't set phoneNumberError - no validation error styling
                setIsLoading(false);
                return;
            }

            // Check for other specific error types
            if (errorMessage.includes('Name verification is already in progress')) {
                const msg = 'A name verification process is already running for this business. Please wait for it to complete before trying again. This usually takes a few minutes.';
                showNotification('warning', 'Verification in Progress', msg);
                if (Platform.OS !== 'web') {
                    Alert.alert('Verification in Progress', msg, [{ text: 'OK', style: 'default', onPress: () => { setPhoneNumberError(errorMessage); } }]);
                } else {
                    setPhoneNumberError(errorMessage);
                }
                setIsLoading(false);
                return;
            }

            if (errorMessage.includes('Business name validation failed')) {
                const msg = 'Your business name contains invalid characters. Please use only letters, numbers, and spaces. Avoid special characters like @, #, $, etc.';
                showNotification('error', 'Invalid Business Name', msg);
                if (Platform.OS !== 'web') {
                    Alert.alert('Invalid Business Name', msg, [{ text: 'OK', style: 'default', onPress: () => { setPhoneNumberError(errorMessage); } }]);
                } else {
                    setPhoneNumberError(errorMessage);
                }
                setIsLoading(false);
                return;
            }

            if (errorMessage.includes('Invalid phone number format')) {
                const msg = 'The phone number format is incorrect. Please check your phone number and try again.';
                showNotification('error', 'Invalid Phone Number', msg);
                if (Platform.OS !== 'web') {
                    Alert.alert('Invalid Phone Number', msg, [{ text: 'OK', style: 'default', onPress: () => { setPhoneNumberError(errorMessage); } }]);
                } else {
                    setPhoneNumberError(errorMessage);
                }
                setIsLoading(false);
                return;
            }

            if (errorMessage.includes('not available for WhatsApp Business')) {
                const msg = 'This phone number cannot be used for WhatsApp Business. Please try a different number.';
                showNotification('error', 'Phone Number Not Available', msg);
                if (Platform.OS !== 'web') {
                    Alert.alert('Phone Number Not Available', msg, [{ text: 'OK', style: 'default', onPress: () => { setPhoneNumberError(errorMessage); } }]);
                } else {
                    setPhoneNumberError(errorMessage);
                }
                setIsLoading(false);
                return;
            }

            if (errorMessage.includes('App permissions error')) {
                const msg = 'Your WhatsApp Business App needs proper permissions to register phone numbers. Please contact support to ensure your app is approved by Meta and has the required permissions.';
                showNotification('error', 'App Permissions Error', msg);
                if (Platform.OS !== 'web') {
                    Alert.alert('App Permissions Error', msg, [
                        { text: 'Contact Support', onPress: () => { console.log('User wants to contact support'); } },
                        { text: 'OK', style: 'default', onPress: () => { setPhoneNumberError(errorMessage); } }
                    ]);
                } else {
                    setPhoneNumberError(errorMessage);
                }
                setIsLoading(false);
                return;
            }

            // Generic error - show notification and alert for all other errors
            const genericErrorMsg = errorMessage || 'Failed to register phone number. Please try again.';
            showNotification('error', errorTitle, genericErrorMsg);
            if (Platform.OS !== 'web') {
                Alert.alert(errorTitle, genericErrorMsg, [{ text: 'OK', onPress: () => { setPhoneNumberError(genericErrorMsg); } }]);
            } else {
                setPhoneNumberError(genericErrorMsg);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleOTPSubmit = async () => {
        if (!otp.trim() || otp.length < 6) {
            setOtpError('Please enter the 6-digit verification code');
            return;
        }

        setOtpError('');
        setIsLoading(true);

        try {
            // Call API to verify phone number
            const phoneNumberForVerification = submittedPhoneData?.formattedNumber || phoneNumber;
            console.log('🔍 [WhatsAppBusinessSetup] OTP verification - phoneNumber:', phoneNumberForVerification, 'submittedPhoneData:', submittedPhoneData);
            const response = await apiService.verifyWhatsAppPhoneNumber(registrationId, otp.trim(), phoneNumberForVerification);

            if (response.data) {
                // Use newToken from verify response (e.g. demo backdoor 230192) or refresh tokens
                try {
                    const newToken = (response.data as any).newToken;
                    const newRefreshToken = (response.data as any).newRefreshToken;
                    if (newToken) {
                        console.log('✅ [WhatsAppBusinessSetup] Using newToken from verify response (e.g. demo complete)');
                        const refreshToUse = newRefreshToken || apiService.getRefreshToken() || tokens?.refreshToken || '';
                        apiService.setAuthToken(newToken);
                        apiService.setRefreshToken(refreshToUse);
                        await updateAuthTokens(
                            { token: newToken, refreshToken: refreshToUse },
                            'service-setup'
                        );
                        syncApiServiceWithStore({ token: newToken, refreshToken: refreshToUse });
                    } else {
                        console.log('🔄 [WhatsAppBusinessSetup] Refreshing authentication token after verification...');
                        const refreshResult = await tokenManager.refreshTokens();
                        if (refreshResult) {
                            console.log('✅ [WhatsAppBusinessSetup] Token refreshed successfully via token manager');
                            apiService.setAuthToken(refreshResult.token);
                            apiService.setRefreshToken(refreshResult.refreshToken);
                            syncApiServiceWithStore({
                                token: refreshResult.token,
                                refreshToken: refreshResult.refreshToken
                            });
                        } else {
                            console.warn('⚠️ [WhatsAppBusinessSetup] Token refresh returned null, trying direct API call...');
                            const refreshResult = await apiService.refreshAuthToken(apiService.getRefreshToken() || '');
                            if (refreshResult.data) {
                                console.log('✅ [WhatsAppBusinessSetup] Token refreshed successfully via direct API call');
                                apiService.setAuthToken(refreshResult.data.token);
                                apiService.setRefreshToken(refreshResult.data.refreshToken);
                                syncApiServiceWithStore({
                                    token: refreshResult.data.token,
                                    refreshToken: refreshResult.data.refreshToken
                                });
                            }
                        }
                    }
                } catch (refreshError) {
                    console.warn('⚠️ [WhatsAppBusinessSetup] Token refresh failed, but verification was successful:', refreshError);
                }

                setCurrentStep('success');
                notificationService.showNewMessageNotification(
                    registrationId,
                    `WhatsApp Business (${phoneNumber})`,
                    'WhatsApp Business setup complete! Your phone number has been successfully verified and is now ready for business use.',
                    'whatsapp'
                );

                // Wait 2 seconds then redirect (Home by default, or the stepper's next step
                // when embedded in onboarding)
                setTimeout(() => {
                    navigateTo(onCompleteScreen, undefined, onCompleteParams);
                }, 2000);
            } else {
                setOtpError('Invalid verification code. Please check and try again.');
            }

        } catch (error: any) {
            console.error('WhatsApp verification error:', error);
            
            // Check for rate limit error first (before setting any field errors)
            if (error.statusCode === 429 || 
                error.message?.includes('too many') || 
                error.message?.includes('rate limit') ||
                error.message?.includes('RATE_LIMITED') ||
                error.message?.includes('Too many verification attempts') ||
                error.errors?.[0]?.errorCode === 'RATE_LIMITED' ||
                error.errors?.[0]?.errorCode === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
                const retryAfter = error.errors?.[0]?.retryAfter || 300; // 5 minutes default
                const retryMinutes = Math.ceil(retryAfter / 60);
                const errorDetails = error.errors?.[0]?.details || error.message || 'You\'ve tried too many verification codes. Please wait a while before trying again.';
                
                // Set rate limiting state
                setIsRateLimited(true);
                setRateLimitRetryAfter(retryAfter);
                
                // Show as notification instead of field error
                showNotification(
                    'error', 
                    'Too Many Attempts', 
                    errorDetails
                );
                
                // Clear field error
                setOtpError('');
                
                // Start countdown timer
                const countdownInterval = setInterval(() => {
                    setRateLimitRetryAfter(prev => {
                        if (prev <= 1) {
                            clearInterval(countdownInterval);
                            setIsRateLimited(false);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                // Check for phone number already registered error
                const errorMessage = error.message || '';
                if (errorMessage.includes('already registered') ||
                    errorMessage.includes('already connected') ||
                    errorMessage.includes('disconnect it from the existing account') ||
                    error.data?.message?.includes('already registered') ||
                    error.errors?.[0]?.message?.includes('already registered') ||
                    error.errors?.[0]?.errorCode === 'PHONE_ALREADY_REGISTERED') {
                    Alert.alert(
                        'Phone Number Already Registered',
                        'This phone number is already registered to a WhatsApp account. Please disconnect it from the existing account and wait up to 3 minutes before trying again.',
                        [
                            {
                                text: 'I Understand',
                                onPress: () => {
                                    setOtpError('Please disconnect this number from the existing WhatsApp account first, then wait 3 minutes before trying again.');
                                }
                            },
                            {
                                text: 'Use Different Number',
                                onPress: () => {
                                    handleBackToPhone();
                                }
                            }
                        ]
                    );
                } else if (error.statusCode === 400 && 
                           (error.errors?.[0]?.errorCode === 'INVALID_VERIFICATION_CODE' ||
                            error.message?.includes('Invalid verification code') ||
                            error.message?.includes('incorrect'))) {
                    // Invalid verification code error
                    const errorMessage = error.errors?.[0]?.message || error.message || 'Invalid verification code. Please check and try again.';
                    setOtpError(errorMessage);
                } else {
                    // Only set field error if it's not a rate limit error
                    if (!errorMessage.includes('Too many verification attempts') && 
                        !errorMessage.includes('too many') && 
                        !errorMessage.includes('rate limit')) {
                        setOtpError(error.message || 'Failed to verify phone number. Please try again.');
                    } else {
                        // Show rate limit as notification instead
                        showNotification(
                            'error', 
                            'Too Many Attempts', 
                            error.message || 'You\'ve tried too many verification codes. Please wait a while before trying again.'
                        );
                    }
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRequestVoiceCall = async () => {
        console.log('🔍 [WhatsAppBusinessSetup] Voice call - registrationId:', registrationId);
        if (!registrationId) {
            showNotification('error', 'WhatsApp Business Setup', 'No phone number ID available for voice call request.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiService.requestWhatsAppVoiceCall(registrationId);
            
            if (response.data) {
                setCodeMethod('VOICE');
                notificationService.showNewMessageNotification(
                    registrationId,
                    `WhatsApp Business (${phoneNumber})`,
                    'Voice call verification code requested! Please answer the call.',
                    'whatsapp'
                );
            } else {
                showNotification('error', 'Error', 'Failed to request voice call. Please try again.');
            }
        } catch (error: any) {
            console.error('Voice call request error:', error);
            showNotification('error', 'Error', error.message || 'Failed to request voice call. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendSMS = async () => {
        if (!registrationId) {
            showNotification('error', 'Error', 'No phone number ID available for SMS resend.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiService.resendWhatsAppSMS(registrationId);
            
            if (response.data) {
                setCodeMethod('SMS');
                notificationService.showNewMessageNotification(
                    registrationId,
                    `WhatsApp Business (${phoneNumber})`,
                    'New SMS verification code sent to your phone number.',
                    'whatsapp'
                );
            } else {
                showNotification('error', 'Error', 'Failed to resend SMS. Please try again.');
            }
        } catch (error: any) {
            console.error('SMS resend error:', error);
            
            // Check for phone number already registered error
            const isPhoneAlreadyRegistered = error.message?.includes('already registered') ||
                                           error.message?.includes('already connected') ||
                                           error.message?.includes('disconnect it from the existing account') ||
                                           error.data?.message?.includes('already registered') ||
                                           error.errors?.[0]?.message?.includes('already registered') ||
                                           error.errors?.[0]?.errorCode === 'PHONE_ALREADY_REGISTERED';
            
            if (isPhoneAlreadyRegistered) {
                showNotification(
                    'error',
                    'Phone Number Already Registered',
                    'This phone number is already registered to a WhatsApp account. Please disconnect it from the existing account and wait up to 3 minutes before trying again.'
                );
            } else if (error.statusCode === 429 || 
                       error.message?.includes('too many') || 
                       error.message?.includes('rate limit') ||
                       error.message?.includes('RATE_LIMITED') ||
                       error.errors?.[0]?.errorCode === 'RATE_LIMITED' ||
                       error.errors?.[0]?.errorCode === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
                const retryAfter = error.errors?.[0]?.retryAfter || 300; // 5 minutes default
                const retryMinutes = Math.ceil(retryAfter / 60);
                const errorDetails = error.errors?.[0]?.details || error.message || 'You\'ve tried too many verification codes. Please wait a while before trying again.';
                
                // Set rate limiting state
                setIsRateLimited(true);
                setRateLimitRetryAfter(retryAfter);
                
                showNotification(
                    'error', 
                    'Too Many Attempts', 
                    errorDetails
                );
                
                // Start countdown timer
                const countdownInterval = setInterval(() => {
                    setRateLimitRetryAfter(prev => {
                        if (prev <= 1) {
                            clearInterval(countdownInterval);
                            setIsRateLimited(false);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                showNotification('error', 'Error', error.message || 'Failed to resend SMS. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToPhone = () => {
        setCurrentStep('phone');
        setOtp('');
        setOtpError('');
        setSubmittedPhoneData(null); // Clear stored phone data
    };

    // renderPhoneStep removed - functionality moved to WhatsAppPhoneList component and renderPhoneRegistrationForm

    // Auto-focus first OTP input when step changes to OTP
    useEffect(() => {
        if (currentStep === 'otp' && otpInputRefs.current[0]) {
            const timer = setTimeout(() => {
                otpInputRefs.current[0]?.focus();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [currentStep]);

    const renderOTPStep = () => {
        const displayPhoneNumber = submittedPhoneData
            ? `${submittedPhoneData.dialCode}${submittedPhoneData.nationalNumber}`
            : phoneNumber || 'your phone number';

        const formBody = (
            <>
                <Text style={styles.label}>Verification code</Text>
                <Text style={styles.otpSubtitle}>Enter the 6-digit code sent to {displayPhoneNumber}</Text>
                
                {/* Compact OTP Input */}
                <View style={styles.otpInputContainer}>
                    {[0, 1, 2, 3, 4, 5].map((index) => {
                        const digit = (otp[index] || '').replace(/\D/g, '');
                        return (
                            <TextInput
                                key={index}
                                ref={(ref) => {
                                    otpInputRefs.current[index] = ref;
                                }}
                                style={[
                                    styles.otpInputBox,
                                    digit && styles.otpInputBoxFilled,
                                    otpError && styles.otpInputBoxError,
                                ]}
                                value={digit}
                                onChangeText={(value) => {
                                    const cleanValue = value.replace(/\D/g, '');
                                    if (cleanValue.length > 1) {
                                        // Handle paste - fill all inputs
                                        const pasted = cleanValue.slice(0, 6).split('');
                                        const newOtp = [...otp.split('')];
                                        pasted.forEach((char, i) => {
                                            if (i < 6) newOtp[i] = char;
                                        });
                                        setOtp(pasted.join(''));
                                        // Focus last filled or next empty
                                        const nextIndex = Math.min(pasted.length, 5);
                                        otpInputRefs.current[nextIndex]?.focus();
                                    } else if (cleanValue) {
                                        // Single digit entered
                                        const newOtp = otp.split('');
                                        newOtp[index] = cleanValue;
                                        setOtp(newOtp.join('').slice(0, 6));
                                        // Focus next if available
                                        if (index < 5) {
                                            otpInputRefs.current[index + 1]?.focus();
                                        }
                                    } else {
                                        // Backspace - clear current
                                        const newOtp = otp.split('');
                                        newOtp[index] = '';
                                        setOtp(newOtp.join(''));
                                    }
                                }}
                                onKeyPress={(e) => {
                                    if (e.nativeEvent.key === 'Backspace' && !digit && index > 0) {
                                        otpInputRefs.current[index - 1]?.focus();
                                    }
                                }}
                                keyboardType="number-pad"
                                maxLength={1}
                                editable={!isLoading}
                                selectTextOnFocus
                            />
                        );
                    })}
                </View>
                
                {otpError ? <Text style={styles.errorText}>{otpError}</Text> : null}
                
                <Text style={[styles.helpText, { textAlign: 'left' }]}>
                    Meta has sent a verification code to your phone number.
                </Text>
                
                <View style={styles.resendContainer}>
                    <Text style={styles.resendTitle}>Didn&apos;t receive the code?</Text>
                    <View style={styles.resendButtons}>
                        <TouchableOpacity
                            style={[styles.resendButton, (isLoading || isRateLimited) && styles.buttonDisabled]}
                            onPress={handleResendSMS}
                            disabled={isLoading || isRateLimited}
                        >
                            <Ionicons name="mail" size={14} color={isRateLimited ? colors.textLight : colors.primary} />
                            <Text style={[styles.resendButtonText, isRateLimited && { color: colors.textLight }]}>
                                {isRateLimited ? `Wait ${Math.ceil(rateLimitRetryAfter / 60)}m` : 'Resend SMS'}
                            </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.resendButton, (isLoading || isRateLimited) && styles.buttonDisabled]}
                            onPress={handleRequestVoiceCall}
                            disabled={isLoading || isRateLimited}
                        >
                            <Ionicons name="call" size={14} color={isRateLimited ? colors.textLight : colors.primary} />
                            <Text style={[styles.resendButtonText, isRateLimited && { color: colors.textLight }]}>
                                Voice Call
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
                
                <View style={styles.buttonContainer}>
                    <TouchableOpacity style={styles.secondaryButton} onPress={handleBackToPhone}>
                        <Text style={styles.secondaryButtonText}>Back</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.primaryButton, isLoading && styles.buttonDisabled, { flex: 1 }]}
                        onPress={handleOTPSubmit}
                        disabled={isLoading || otp.length < 6}
                    >
                        {isLoading ? (
                            <Text style={styles.buttonText}>Verifying...</Text>
                        ) : (
                            <Text style={styles.buttonText}>Verify</Text>
                        )}
                    </TouchableOpacity>
                </View>
            </>
        );

        return (
            <ScrollView
                style={[styles.formContainer, Platform.OS !== 'web' && { flex: 1 }]}
                contentContainerStyle={[styles.formContent, Platform.OS !== 'web' && { paddingBottom: 100 }]}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.formHeader}>
                    <Text style={styles.formTitle}>Verify Phone Number</Text>
                    <TouchableOpacity onPress={handleBackToPhone} style={styles.closeButton}>
                        <Ionicons name="close" size={24} color={colors.text.secondary} />
                    </TouchableOpacity>
                </View>
                {formBody}
            </ScrollView>
        );
    };

    const renderSuccessStep = () => (
        <View style={styles.stepContainer}>
            <View style={styles.iconContainer}>
                <Ionicons name="checkmark-circle" size={64} color="#25D366" />
            </View>

            <Text style={styles.title}>Setup Complete!</Text>
            <Text style={styles.subtitle}>
                Your WhatsApp Business account is now ready to use
            </Text>

            <View style={styles.successInfo}>
                <View style={styles.successItem}>
                    <Ionicons name="checkmark-circle" size={20} color="#25D366" />
                    <Text style={styles.successText}>Phone number verified with Meta</Text>
                </View>
                <View style={styles.successItem}>
                    <Ionicons name="checkmark-circle" size={20} color="#25D366" />
                    <Text style={styles.successText}>WhatsApp Business API connected</Text>
                </View>
                <View style={styles.successItem}>
                    <Ionicons name="checkmark-circle" size={20} color="#25D366" />
                    <Text style={styles.successText}>Ready to send business messages</Text>
                </View>
            </View>

            <Text style={styles.redirectText}>
                Redirecting to home screen...
            </Text>
        </View>
    );

    const handleTabPress = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });

    // Handle phone card actions
    const handlePhonePress = (phone: WhatsAppPhoneCardData) => {
        setSelectedPhone(phone);
        setShowPhoneForm(false); // Close form if open
        if (Platform.OS === 'web') {
            // Right pane will show automatically based on selectedPhone
        }
    };

    const handlePhoneVerify = (phone: WhatsAppPhoneCardData) => {
        setSelectedPhone(phone);
        setCurrentStep('otp');
        setOtp(''); // Clear any previous OTP
        setOtpError(''); // Clear any previous errors
        setRegistrationId(phone.id);
        setSubmittedPhoneData({
            dialCode: phone.displayPhoneNumber?.startsWith('+') ? phone.displayPhoneNumber.substring(0, phone.displayPhoneNumber.indexOf(' ')) : '+',
            nationalNumber: phone.displayPhoneNumber?.replace(/^\+\d+\s*/, '') || '',
            formattedNumber: phone.displayPhoneNumber || phone.whatsapp_phone_number || ''
        });
        if (Platform.OS === 'web') {
            setShowRightPane(false);
        }
    };

    const handleAddNewPhone = () => {
        setCurrentStep('phone');
        setShowPhoneForm(true);
        setSelectedPhone(null);
        if (Platform.OS === 'web') {
            setShowRightPane(false);
        }
    };

    const handleCloseRightPane = () => {
        setShowRightPane(false);
        setSelectedPhone(null);
        if (currentStep === 'otp') {
            setCurrentStep('phone');
        }
    };

    // Deleting a number lets the business reconnect it (or a different one) via embedded
    // signup again — the main recovery path for a number stuck "Pending" from an
    // incomplete/failed connection.
    const handleDeletePhoneRequest = (phoneId: string) => {
        const phone = allPhones.find(p => p.id === phoneId) || null;
        setPhoneToDelete(phone);
    };

    const confirmDeletePhone = async () => {
        if (!phoneToDelete) return;
        setIsDeletingPhone(true);
        try {
            const response = await apiService.deleteWhatsAppPhoneNumber(phoneToDelete.id);
            if (response.statusCode === 1000) {
                showNotification('success', 'Number Removed', 'The phone number has been disconnected. You can now reconnect it or add a different one.');
                setPhoneToDelete(null);
                if (selectedPhone?.id === phoneToDelete.id) {
                    handleCloseRightPane();
                }
                await fetchPhoneNumbers();
            } else {
                showNotification('error', 'Could Not Remove Number', response.errors?.[0]?.message || 'Please try again.');
            }
        } catch (error: any) {
            showNotification('error', 'Could Not Remove Number', error?.message || 'Please try again.');
        } finally {
            setIsDeletingPhone(false);
        }
    };

    // A number connected via embedded signup can be linked to our app but still "Pending"
    // on the Cloud API if the initial registration step didn't complete — this lets the
    // business finish registering it directly instead of having to delete/reconnect.
    const handleRegisterPendingPhone = async (phone: WhatsAppPhoneCardData) => {
        setRegisteringPhoneId(phone.id);
        try {
            const response = await apiService.registerPendingWhatsAppPhoneNumber(phone.id);
            if (response.statusCode === 1000) {
                showNotification('success', 'Number Registered', 'This phone number is now active for messaging.');
                await fetchPhoneNumbers();
            } else {
                showNotification('error', 'Registration Failed', response.errors?.[0]?.message || 'Please try again.');
            }
        } catch (error: any) {
            showNotification('error', 'Registration Failed', error?.message || 'Please try again.');
        } finally {
            setRegisteringPhoneId(null);
        }
    };

    // Combine API phones and token phones for display, avoiding duplicates
    const phoneMap = new Map<string, WhatsAppPhoneCardData>();
    
    // First, add API phones (these are the source of truth)
    phoneNumbers.forEach(phone => {
        const phoneId = phone.id || phone.whatsapp_phone_number_id;
        if (phoneId) {
            phoneMap.set(phoneId, {
                id: phoneId,
                displayPhoneNumber: phone.displayPhoneNumber,
                whatsapp_phone_number: phone.displayPhoneNumber,
                whatsapp_phone_number_id: phone.id,
                status: phone.status,
                isVerified: phone.isVerified || phone.status === 'verified' || phone.status === 'VERIFIED',
                verifiedBy: phone.verifiedBy,
                verifiedName: phone.verifiedName,
                qualityRating: phone.qualityRating,
                qualityFlagged: phone.qualityFlagged,
                currentMessagingLimit: phone.currentMessagingLimit,
                codeVerificationStatus: phone.codeVerificationStatus,
            });
        }
    });
    
    // Then add token phones only if they don't already exist in the map
    existingPhoneNumbers.forEach((phone, index) => {
        const phoneId = phone.whatsapp_phone_number_id || phone.whatsapp_phone_number;
        if (phoneId && !phoneMap.has(phoneId)) {
            phoneMap.set(phoneId, {
                id: phoneId,
                displayPhoneNumber: phone.whatsapp_phone_number,
                whatsapp_phone_number: phone.whatsapp_phone_number,
                whatsapp_phone_number_id: phone.whatsapp_phone_number_id,
                status: phone.status,
                isVerified: phone.status === 'verified',
                is_default: phone.is_default,
            });
        }
    });
    
    const allPhones: WhatsAppPhoneCardData[] = Array.from(phoneMap.values());

    // Templates can only be submitted once at least one number is actually connected and
    // registered for messaging — otherwise Meta has no verified WABA/number to submit against.
    const hasVerifiedWhatsAppNumber = allPhones.some(phone =>
        phone.isVerified || phone.status === 'verified' || phone.status === 'VERIFIED' || phone.codeVerificationStatus === 'VERIFIED'
    );


    // WhatsAppPhoneSettings owns the entire save flow itself now (channel-settings doc
    // upsert + flow attachment create/update/delete) — this callback just confirms to the
    // user once that's done, rather than duplicating the upsert here.
    const handlePhoneSettingsSave = async (settings: PhoneSettingsData) => {
        showNotification('success', 'Success', 'Phone settings saved successfully.');
    };

    const renderPhoneStep = () => (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
        >
            {/* Business Display Section */}
            <View style={styles.businessSection}>
                <View style={styles.businessHeader}>
                    <Ionicons name="business" size={20} color={colors.primary} />
                    <Text style={styles.businessSectionTitle}>Current Business</Text>
                </View>
                <View style={styles.currentBusiness}>
                    <Text style={styles.businessName}>{businessName}</Text>
                    <Text style={styles.businessRole}>Owner</Text>
                </View>
            </View>

            {/* Message Templates Section — dimmed/disabled until a number is connected and registered */}
            <TouchableOpacity
                style={[styles.businessSection, !hasVerifiedWhatsAppNumber && styles.businessSectionDisabled]}
                onPress={() => {
                    if (!hasVerifiedWhatsAppNumber) {
                        showNotification('warning', 'No Connected Number', 'Connect and register a WhatsApp phone number before managing templates.');
                        return;
                    }
                    navigateTo('template-management');
                }}
                activeOpacity={hasVerifiedWhatsAppNumber ? 0.7 : 1}
            >
                <View style={styles.businessHeader}>
                    <Ionicons name="document-text-outline" size={20} color={hasVerifiedWhatsAppNumber ? colors.primary : colors.text.secondary} />
                    <Text style={[styles.businessSectionTitle, !hasVerifiedWhatsAppNumber && styles.businessSectionTitleDisabled]}>Message Templates</Text>
                    <DirectionalIcon name="chevron-forward" size={18} color={colors.text.secondary} style={{ marginLeft: 'auto' }} />
                </View>
                <Text style={styles.businessRole}>
                    {hasVerifiedWhatsAppNumber
                        ? 'Submit templates for Meta approval and track their status'
                        : 'Connect and register a phone number first'}
                </Text>
            </TouchableOpacity>

            {/* Phone Numbers Section - At the top */}
            <View style={styles.phoneNumbersSection}>
                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitle}>Registered Phone Numbers</Text>
                    {/* Old "Add" button (registers a new number under our shared WABA) hidden
                        per request — kept wired (setShowPhoneForm/handleAddNewPhone, the
                        phone/otp/success step machine) as a fallback for clients who can't pass
                        Meta's Business verification gate on the embedded-signup flow below.
                        Re-enable by uncommenting. */}
                    {false && canAddMoreNumbers && (
                        <TouchableOpacity
                            style={styles.addButton}
                            onPress={() => {
                                setSelectedPhone(null); // Clear selected phone first
                                setShowPhoneForm(true); // Then show the form
                            }}
                            activeOpacity={0.7}
                        >
                            <Ionicons name="add" size={20} color={colors.white} />
                            <Text style={styles.addButtonText}>Add</Text>
                        </TouchableOpacity>
                    )}
                </View>

                {canAddMoreNumbers && (
                    <TouchableOpacity
                        style={styles.connectOwnWabaButton}
                        onPress={() => setShowEmbeddedSignup(true)}
                        activeOpacity={0.7}
                    >
                        <Ionicons name="logo-whatsapp" size={18} color={colors.primary} />
                        <Text style={styles.connectOwnWabaButtonText}>Connect your own WhatsApp Business Account</Text>
                    </TouchableOpacity>
                )}

                {isLoadingPhoneNumbers ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" color={colors.primary} />
                        <Text style={styles.loadingText}>Loading phone numbers...</Text>
                    </View>
                ) : allPhones.length > 0 ? (
                    <>
                        {allPhones.map((phone) => {
                            const isSelected = selectedPhone?.id === phone.id;
                            return (
                                <TouchableOpacity
                                    key={phone.id}
                                    onPress={() => handlePhonePress(phone)}
                                    activeOpacity={0.7}
                                >
                                    <WhatsAppPhoneCard
                                        phone={phone}
                                        onPress={handlePhonePress}
                                        onVerify={handlePhoneVerify}
                                        onRegister={handleRegisterPendingPhone}
                                        isRegistering={registeringPhoneId === phone.id}
                                        onDelete={handleDeletePhoneRequest}
                                        showActions={true}
                                    />
                                </TouchableOpacity>
                            );
                        })}
                    </>
                ) : (
                    <View style={styles.emptyPhonesContainer}>
                        <Ionicons name="call-outline" size={48} color={colors.text.secondary} />
                        <Text style={styles.emptyPhonesText}>No phone numbers registered</Text>
                        <Text style={styles.emptyPhonesSubtext}>
                            Add your first WhatsApp Business phone number to get started
                        </Text>
                    </View>
                )}

                {/* Phone Limit Reached Message */}
                {!canAddMoreNumbers && (
                    <View style={styles.limitReachedSection}>
                        <Ionicons name="warning-outline" size={20} color={colors.warning} />
                        <Text style={styles.limitReachedText}>
                            You have reached the maximum number of WhatsApp phone numbers ({phoneNumberLimit}) allowed by your subscription plan.
                        </Text>
                    </View>
                )}
            </View>

            {/* Settings Section - Below phone numbers */}
            {selectedPhone && (
                <View style={styles.settingsSection}>
                    <WhatsAppPhoneSettings
                        phone={selectedPhone}
                        onSave={handlePhoneSettingsSave}
                    />
                </View>
            )}

            {!selectedPhone && allPhones.length > 0 && (
                <View style={styles.noSelectionContainer}>
                    <Ionicons name="settings-outline" size={48} color={colors.text.secondary} />
                    <Text style={styles.noSelectionText}>Select a phone number to configure settings</Text>
                </View>
            )}
        </ScrollView>
    );

    // Render right pane content (registration/OTP forms or phone settings) - only on web
    const renderRightPaneContent = () => {
        if (currentStep === 'otp') {
            return renderOTPStep();
        }
        if (currentStep === 'success') {
            return renderSuccessStep();
        }
        // Show phone registration form if showPhoneForm is true (prioritize form over settings)
        if (showPhoneForm) {
            // Show phone registration form in right pane
            return (
                <ScrollView
                    style={styles.formContainer}
                    contentContainerStyle={styles.formContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.formHeader}>
                        <Text style={styles.formTitle}>New Phone Number</Text>
                        <TouchableOpacity onPress={() => {
                            setShowPhoneForm(false);
                        }} style={styles.closeButton}>
                            <Ionicons name="close" size={24} color={colors.text.secondary} />
                        </TouchableOpacity>
                    </View>

                    <Text style={styles.label}>Phone Number</Text>
                    <PhoneInputComponent
                        ref={phoneInputRef}
                        value={phoneNumber}
                        onChangeText={(text: string) => {
                            setPhoneNumber(text);
                            setPhoneNumberError(''); // Clear error on input change
                        }}
                        placeholder="Enter phone number"
                        error={phoneNumberError}
                    />

                    <Text style={styles.helpText}>
                        This phone number will be registered with Meta's WhatsApp Business API.
                        If it's already in use on your personal WhatsApp app, you'll need to remove it first.
                    </Text>

                    <View style={styles.buttonContainer}>
                        <TouchableOpacity
                            style={styles.secondaryButton}
                            onPress={() => {
                                setShowPhoneForm(false);
                            }}
                        >
                            <Text style={styles.secondaryButtonText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.primaryButton, isLoading && styles.buttonDisabled, { flex: 1 }]}
                            onPress={handlePhoneSubmit}
                            disabled={isLoading || !phoneNumber.trim()}
                        >
                            {isLoading ? (
                                <Text style={styles.buttonText}>Registering...</Text>
                            ) : (
                                <Text style={styles.buttonText}>Register</Text>
                            )}
                        </TouchableOpacity>
                    </View>
                </ScrollView>
            );
        }
        // Settings live only in the middle pane (below phone list); no duplication in right pane
        return null;
    };

    return (
        <>
        <ScreenLayout
            header={
                <Header
                    showNotifications={false}
                    showMessages={false}
                    showNavigation={Platform.OS === 'web'}
                    activeTab={activeTab}
                    onTabPress={handleTabPress}
                />
            }
            showBottomTabs={Platform.OS !== 'web'}
            showLeftPane={showLeftPane}
            onLeftPaneClose={() => setShowLeftPane(false)}
            leftPaneContent="settings"
            onSettingsItemPress={async (itemId) => {
                const { setPendingSettingsItem } = await import('../utils/tempStorage');
                await setPendingSettingsItem(itemId);
                navigateTo('settings');
            }}
            showRightPane={Platform.OS === 'web'}
            rightPaneContent="custom"
            rightPaneCustomContent={(() => {
                const content = renderRightPaneContent();
                if (content) {
                    return content;
                }
                // Show placeholder when no specific content
                return (
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                        <Ionicons name="call-outline" size={64} color={colors.text.secondary} />
                        <Text style={{ color: colors.text.secondary, fontSize: 16, marginTop: 16, textAlign: 'center' }}>
                            {allPhones.length > 0 
                                ? 'Select a phone number to configure settings or click "Add" to register a new number'
                                : 'Click "Add" to register your first WhatsApp Business phone number'
                            }
                        </Text>
                    </View>
                );
            })()}
            onRightPaneClose={() => {
                setShowPhoneForm(false);
                setSelectedPhone(null);
                if (currentStep === 'otp') {
                    setCurrentStep('phone');
                }
            }}
        >
            <StepperReturnBanner />
            {(currentStep === 'phone' || (Platform.OS === 'web' && (currentStep === 'otp' || currentStep === 'success'))) && renderPhoneStep()}
            {currentStep === 'otp' && Platform.OS !== 'web' && renderOTPStep()}
            {currentStep === 'success' && Platform.OS !== 'web' && (
                <ScrollView
                    style={styles.container}
                    contentContainerStyle={styles.contentContainer}
                    showsVerticalScrollIndicator={false}
                >
                    {renderSuccessStep()}
                </ScrollView>
            )}

            {/* More Options Modal */}
            <MoreOptionsModal
                visible={showMoreModal}
                onClose={() => setShowMoreModal(false)}
            />
        </ScreenLayout>

        {showEmbeddedSignup && (
            <Modal visible={showEmbeddedSignup} animationType="slide" onRequestClose={() => setShowEmbeddedSignup(false)}>
                <SafeAreaView style={styles.embeddedSignupContainer}>
                    <View style={styles.embeddedSignupHeader}>
                        <Text style={styles.embeddedSignupTitle}>Connect WhatsApp</Text>
                        <TouchableOpacity onPress={() => setShowEmbeddedSignup(false)}>
                            <Ionicons name="close" size={26} color={colors.text.secondary} />
                        </TouchableOpacity>
                    </View>
                    {Platform.OS === 'web' ? (
                        <iframe
                            src={embeddedSignupUrl}
                            style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
                            title="Connect WhatsApp Business Account"
                        />
                    ) : WebView ? (
                        <WebView
                            source={{ uri: embeddedSignupUrl }}
                            onMessage={handleEmbeddedSignupWebViewMessage}
                            startInLoadingState
                            renderLoading={() => (
                                <View style={styles.loadingContainer}>
                                    <ActivityIndicator size="large" color={colors.primary} />
                                    <Text style={styles.loadingText}>Loading...</Text>
                                </View>
                            )}
                        />
                    ) : null}
                </SafeAreaView>
            </Modal>
        )}

        <ConfirmationModal
            visible={!!phoneToDelete}
            onClose={() => setPhoneToDelete(null)}
            onConfirm={confirmDeletePhone}
            title="Remove Phone Number"
            message={`Are you sure you want to disconnect ${phoneToDelete?.displayPhoneNumber || 'this number'}? You can reconnect it or add a different one afterwards.`}
            confirmText="Remove"
            cancelText="Cancel"
            type="error"
            loading={isDeletingPhone}
        />
        </>
    );
};

const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background.primary,
    },
    contentContainer: {
        flexGrow: 1,
        paddingHorizontal: isMobileBrowser ? spacing.screenPadding : (Platform.OS === 'web' ? spacing.lg : spacing.screenPadding),
        paddingVertical: spacing.lg,
        paddingBottom: Platform.OS === 'web' ? spacing.lg : 100, // Match HomeScreen padding for bottom tabs on mobile
        ...(Platform.OS === 'web' && !isMobileBrowser && {
            maxWidth: '100%',
            width: '100%',
        }),
    },
    stepContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: Platform.OS === 'web' ? spacing.xl : spacing.lg,
        paddingHorizontal: Platform.OS === 'web' ? 0 : spacing.md,
    },
    iconContainer: {
        marginBottom: spacing.lg,
        padding: spacing.lg,
        borderRadius: 50,
        backgroundColor: colors.background.primary,
        shadowColor: shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
    },
    title: {
        fontSize: typography.fontSize['3xl'],
        fontWeight: typography.fontWeight.bold as any,
        color: colors.textDark,
        textAlign: 'center',
        marginBottom: spacing.sm,
    },
    subtitle: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.normal as any,
        color: colors.textLight,
        textAlign: 'center',
        marginBottom: spacing.xl,
        paddingHorizontal: spacing.lg,
    },
    formContainer: {
        width: '100%',
        maxWidth: Platform.OS === 'web' ? (isMobileBrowser ? 400 : 600) : '100%',
        paddingHorizontal: Platform.OS === 'web' ? 0 : spacing.sm,
    },
    label: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.textDark,
        marginBottom: spacing.xs,
        marginTop: spacing.md,
    },
    input: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: spacing.sm,
        padding: spacing.md,
        fontSize: 16,
        color: colors.textDark,
        backgroundColor: colors.background.surface,
    },
    inputError: {
        borderColor: colors.error,
    },
    errorText: {
        fontSize: typography.fontSize.sm,
        color: colors.error,
        marginTop: spacing.xs,
        marginBottom: spacing.sm,
    },
    helpText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
        textAlign: 'center',
        marginTop: spacing.md,
        marginBottom: spacing.lg,
        lineHeight: 20,
    },
    primaryButton: {
        backgroundColor: colors.primary,
        borderRadius: spacing.sm,
        padding: spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: spacing.md,
    },
    buttonDisabled: {
        opacity: 0.6,
    },
    buttonText: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.white,
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: spacing.md,
        marginTop: spacing.md,
    },
    secondaryButton: {
        backgroundColor: colors.background.primary,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: spacing.sm,
        padding: spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    secondaryButtonText: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.textDark,
    },
    successInfo: {
        width: '100%',
        maxWidth: Platform.OS === 'web' ? (isMobileBrowser ? 400 : 600) : '100%',
        marginVertical: spacing.xl,
    },
    successItem: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: spacing.md,
        paddingHorizontal: spacing.lg,
    },
    successText: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.normal as any,
        color: colors.textDark,
        marginLeft: spacing.sm,
    },
    redirectText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
        textAlign: 'center',
        marginTop: spacing.xl,
        fontStyle: 'italic',
    },
    // New styles for refactored layout
    phoneNumbersSection: {
        marginBottom: spacing.xl,
    },
    settingsSection: {
        marginBottom: spacing.xl,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.md,
    },
    sectionTitle: {
        fontSize: typography.fontSize.lg,
        fontFamily: typography.fontFamily.semibold,
        color: colors.text.primary,
    },
    businessHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        marginBottom: spacing.sm,
    },
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: spacing.borderRadius.md,
        backgroundColor: colors.primary,
    },
    addButtonText: {
        fontSize: typography.fontSize.sm,
        fontFamily: typography.fontFamily.semibold,
        color: colors.white,
    },
    emptyPhonesContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.xl * 2,
        paddingHorizontal: spacing.lg,
    },
    emptyPhonesText: {
        fontSize: typography.fontSize.base,
        fontFamily: typography.fontFamily.semibold,
        color: colors.text.primary,
        marginTop: spacing.md,
        marginBottom: spacing.xs,
    },
    emptyPhonesSubtext: {
        fontSize: typography.fontSize.sm,
        color: colors.text.secondary,
        textAlign: 'center',
    },
    noSelectionContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.xl * 2,
        paddingHorizontal: spacing.lg,
        backgroundColor: colors.background.surface,
        borderRadius: spacing.borderRadius.md,
        borderWidth: 1,
        borderColor: colors.border,
    },
    noSelectionText: {
        fontSize: typography.fontSize.base,
        color: colors.text.secondary,
        marginTop: spacing.md,
        textAlign: 'center',
    },
    backButtonContainer: {
        position: 'absolute',
        top: Platform.OS === 'ios' ? spacing.lg + 10 : spacing.lg + 5, // Adjust based on Header height
        left: spacing.md,
        zIndex: 10,
        backgroundColor: colors.background.surface,
        borderRadius: spacing.sm,
        padding: spacing.sm,
        flexDirection: 'row',
        alignItems: 'center',
        shadowColor: shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    backButtonText: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.textDark,
    },
    // New styles for form in right pane
    formHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: spacing.md,
        marginBottom: spacing.lg,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    formTitle: {
        fontSize: typography.fontSize.xl,
        fontFamily: typography.fontFamily.bold,
        color: colors.text.primary,
    },
    formContent: {
        padding: spacing.lg,
    },
    closeButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.secondary,
    },
    // New styles for existing phone numbers and business section
    businessSection: {
        backgroundColor: colors.background.surface,
        borderRadius: spacing.borderRadius.md,
        padding: spacing.md,
        marginBottom: spacing.lg,
        borderWidth: 1,
        borderColor: colors.border,
    },
    businessSectionTitle: {
        fontSize: typography.fontSize.sm,
        fontFamily: typography.fontFamily.medium,
        color: colors.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    businessSectionDisabled: {
        opacity: 0.5,
    },
    businessSectionTitleDisabled: {
        color: colors.text.disabled,
    },
    currentBusiness: {
        gap: spacing.xs,
    },
    businessInfo: {
        flex: 1,
    },
    businessName: {
        fontSize: typography.fontSize.base,
        fontFamily: typography.fontFamily.semibold,
        color: colors.text.primary,
    },
    businessRole: {
        fontSize: typography.fontSize.sm,
        color: colors.text.secondary,
    },
    existingNumbersSection: {
        width: '100%',
        maxWidth: Platform.OS === 'web' ? (isMobileBrowser ? 400 : 600) : '100%',
        marginBottom: spacing.lg,
    },
    existingNumbersTitle: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.textDark,
        marginBottom: spacing.md,
        textAlign: 'center',
    },
    existingPhoneItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.background.surface,
        padding: spacing.md,
        borderRadius: spacing.sm,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    phoneInfo: {
        flex: 1,
    },
    phoneNumber: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.textDark,
        marginBottom: spacing.xs,
    },
    phoneStatus: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
    },
    verifyButton: {
        backgroundColor: colors.primary,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: spacing.sm,
    },
    verifyButtonText: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.white,
    },
    addNewButton: {
        backgroundColor: colors.background.primary,
        borderWidth: 1,
        borderColor: colors.primary,
        borderRadius: spacing.sm,
        padding: spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: spacing.md,
    },
    addNewButtonText: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.primary,
    },
    phoneFormSection: {
        width: '100%',
        maxWidth: Platform.OS === 'web' ? (isMobileBrowser ? 400 : 600) : '100%',
    },
    phoneFormTitle: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.textDark,
        marginBottom: spacing.md,
        textAlign: 'center',
    },
    limitReachedSection: {
        width: '100%',
        padding: spacing.md,
        backgroundColor: colors.background.secondary,
        borderRadius: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    limitReachedTitle: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.textDark,
        marginBottom: spacing.sm,
        textAlign: 'center',
    },
    limitReachedText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
        textAlign: 'center',
        lineHeight: 18,
    },
    resendContainer: {
        marginVertical: spacing.md,
        alignItems: 'flex-start',
    },
    resendTitle: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
        marginBottom: spacing.sm,
    },
    resendButtons: {
        flexDirection: 'row',
        gap: spacing.sm,
    },
    resendButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.background.secondary,
        borderWidth: 1,
        borderColor: colors.primary,
        borderRadius: spacing.sm,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        gap: spacing.xs,
    },
    resendButtonText: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.primary,
    },
    loadingContainer: {
        padding: spacing.md,
        alignItems: 'center',
    },
    loadingText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
    },
    connectOwnWabaButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: spacing.borderRadius.md,
        borderWidth: 1,
        borderColor: colors.primary,
        backgroundColor: colors.primaryTransparent,
        alignSelf: 'flex-start',
        marginBottom: spacing.md,
    },
    connectOwnWabaButtonText: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium as any,
        color: colors.primary,
    },
    embeddedSignupContainer: {
        flex: 1,
        backgroundColor: colors.background.surface,
    },
    embeddedSignupHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    embeddedSignupTitle: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.bold as any,
        color: colors.textDark,
    },
    phoneStatusContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: spacing.xs,
    },
    verifiedStatus: {
        color: '#4CAF50',
        fontWeight: typography.fontWeight.medium as any,
    },
    pendingStatus: {
        color: '#FF9800',
        fontWeight: typography.fontWeight.medium as any,
    },
    verifiedName: {
        fontSize: typography.fontSize.xs,
        color: colors.textLight,
        marginLeft: spacing.xs,
        fontStyle: 'italic',
    },
    phoneDetails: {
        fontSize: typography.fontSize.xs,
        color: colors.textLight,
        marginTop: spacing.xs,
    },
    metaAdminVerified: {
        fontSize: typography.fontSize.xs,
        color: '#4CAF50',
        fontWeight: typography.fontWeight.medium as any,
        marginTop: spacing.xs,
        fontStyle: 'italic',
    },
    // Compact OTP styles for right pane
    otpSubtitle: {
        fontSize: typography.fontSize.sm,
        color: colors.text.secondary,
        marginBottom: spacing.md,
        fontFamily: typography.fontFamily.primary,
    },
    otpInputContainer: {
        flexDirection: 'row',
        gap: spacing.xs,
        marginBottom: spacing.md,
        justifyContent: 'flex-start',
    },
    otpInputBox: {
        width: 38,
        height: 38,
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: spacing.borderRadius.sm,
        textAlign: 'center',
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.bold as any,
        color: colors.text.primary,
        backgroundColor: colors.background.surface,
        fontFamily: typography.fontFamily.bold,
    },
    otpInputBoxFilled: {
        borderColor: colors.primary,
        backgroundColor: colors.primaryTransparent,
    },
    otpInputBoxError: {
        borderColor: colors.error,
    },
});
